import { useState, useMemo, useCallback, memo, useRef, useEffect } from 'react'
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch'
import { useGallery } from '../context/GalleryContext'
import {
  useVisibleImages,
  useGridPositions,
  useCanvasDimensions,
  useDebouncedTransform
} from '../hooks/useVisibleImages'
import { useLightbox } from '../hooks/useLightbox'
import { galleryConfig } from '../config/galleryConfig'
import type { CanvasTransform } from '../types'
import GalleryImage from './GalleryImage'
import ZoomControls from './ZoomControls'
import AlbumSelector from './AlbumSelector'
import { Lightbox } from './lightbox'

// Memoized image component to prevent unnecessary re-renders
const MemoizedGalleryImage = memo(GalleryImage)

export default function GalleryCanvas() {
  const {
    albums,
    images,
    selectedAlbumId,
    setSelectedAlbumId,
    isLoading,
    likedImageIds,
    showLikedOnly,
    setShowLikedOnly,
    purchasedImageIds,
    showPurchasedOnly,
    setShowPurchasedOnly
  } = useGallery()

  // Filter images based on liked or purchased filter
  // Note: When showLikedOnly is true, backend already returns only liked images
  // but we keep the filter as a safety check for optimistic updates
  const displayImages = useMemo(() => {
    if (showLikedOnly) {
      // Backend returns liked images directly, no need to filter
      // (likedImageIds is updated from the same response)
      return images
    }
    if (showPurchasedOnly) {
      return images.filter(img => purchasedImageIds.has(img.id))
    }
    return images
  }, [images, showLikedOnly, showPurchasedOnly, purchasedImageIds])

  // Ref to control the pan/zoom canvas
  const transformRef = useRef<ReactZoomPanPinchRef>(null)

  // Lightbox state - use displayImages for filtered view
  const lightbox = useLightbox({ images: displayImages, loop: true })

  // Handle image click to open lightbox
  const handleImageClick = useCallback((index: number) => {
    lightbox.open(index)
  }, [lightbox])


  // Calculate initial zoom - higher on mobile for better visibility
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640
  const initialZoom = isMobile ? 0.8 : galleryConfig.canvas.defaultZoom

  // Calculate initial position to center on the gallery
  // On mobile, offset Y a bit to account for album selector at top
  const initialPosX = typeof window !== 'undefined'
    ? -(2000 - window.innerWidth / 2) * initialZoom
    : 0
  const initialPosY = typeof window !== 'undefined'
    ? -(1500 - window.innerHeight / 2 + (isMobile ? 30 : 0)) * initialZoom
    : 0

  const [transform, setTransform] = useState<CanvasTransform>({
    scale: initialZoom,
    positionX: initialPosX,
    positionY: initialPosY
  })

  // Debounce transform for visibility calculations during fast pan/zoom
  const debouncedTransform = useDebouncedTransform(transform, 32)

  // Calculate grid positions for displayed images (responsive)
  const gridPositions = useGridPositions(displayImages)

  // Calculate canvas dimensions
  const canvasDimensions = useCanvasDimensions(gridPositions)

  // Determine which images are visible (uses debounced transform)
  const visibleIds = useVisibleImages(displayImages, debouncedTransform, gridPositions)

  // VIRTUALIZATION: Only render visible images (with original index preserved for lightbox)
  // This reduces React's work from 10,000 components to ~100
  const visibleImagesWithIndex = useMemo(() => {
    return displayImages
      .map((image, index) => ({ image, index, position: gridPositions[index] }))
      .filter(({ image, position }) => position && visibleIds.has(image.id))
  }, [displayImages, gridPositions, visibleIds])

  // Calculate total image count
  const totalImageCount = useMemo(() => {
    return albums.reduce((sum, a) => sum + a.image_count, 0)
  }, [albums])

  // Optimized transform handler
  const handleTransform = useCallback((_: any, state: any) => {
    setTransform({
      scale: state.scale,
      positionX: state.positionX,
      positionY: state.positionY
    })
  }, [])

  // Zoom to the viewed image when lightbox closes
  useEffect(() => {
    // Only trigger when lightbox just closed (isOpen becomes false) and we have a valid index
    if (!lightbox.state.isOpen && !lightbox.state.isClosing && lightbox.lastViewedIndex >= 0) {
      const position = gridPositions[lightbox.lastViewedIndex]
      if (position && transformRef.current) {
        // Calculate center of the image
        const imageCenterX = position.x + position.width / 2
        const imageCenterY = position.y + position.height / 2

        // Zoom level to make the image prominent but show some context
        const isMobileView = window.innerWidth < 640
        const targetScale = isMobileView ? 1.2 : 0.9

        // Calculate position to center the image on screen
        const viewportCenterX = window.innerWidth / 2
        const viewportCenterY = window.innerHeight / 2

        const newPosX = viewportCenterX - imageCenterX * targetScale
        const newPosY = viewportCenterY - imageCenterY * targetScale

        // Animate to the position
        setTimeout(() => {
          transformRef.current?.setTransform(newPosX, newPosY, targetScale, 400)
        }, 100) // Small delay to let the lightbox close animation finish
      }
    }
  }, [lightbox.state.isOpen, lightbox.state.isClosing, lightbox.lastViewedIndex, gridPositions])

  if (isLoading) {
    return (
      <div className="w-full h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-braydin-olive border-t-braydin-olive rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading gallery...</p>
        </div>
      </div>
    )
  }

  if (displayImages.length === 0) {
    return (
      <div className="w-full h-screen bg-gray-900 flex items-center justify-center">
        {/* Album selector even when empty */}
        {albums.length > 0 && (
          <AlbumSelector
            albums={albums}
            selectedAlbumId={selectedAlbumId}
            onSelect={setSelectedAlbumId}
            totalImageCount={totalImageCount}
            likedCount={likedImageIds.size}
            showLikedOnly={showLikedOnly}
            onToggleLiked={setShowLikedOnly}
            purchasedCount={purchasedImageIds.size}
            showPurchasedOnly={showPurchasedOnly}
            onTogglePurchased={setShowPurchasedOnly}
          />
        )}
        <div className="text-center max-w-md px-4">
          <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h2 className="text-white text-xl font-semibold mb-2">
            {showLikedOnly ? 'No liked photos yet' : showPurchasedOnly ? 'No unlocked photos yet' : selectedAlbumId ? 'No photos in this album' : 'No photos yet'}
          </h2>
          <p className="text-gray-400">{showLikedOnly ? 'Heart some photos to see them here!' : showPurchasedOnly ? 'Unlock photos to see them here!' : 'Check back soon for new photos!'}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-screen overflow-hidden bg-gray-900 relative touch-none">
      {/* Album Selector */}
      <AlbumSelector
        albums={albums}
        selectedAlbumId={selectedAlbumId}
        onSelect={setSelectedAlbumId}
        totalImageCount={totalImageCount}
        likedCount={likedImageIds.size}
        showLikedOnly={showLikedOnly}
        onToggleLiked={setShowLikedOnly}
        purchasedCount={purchasedImageIds.size}
        showPurchasedOnly={showPurchasedOnly}
        onTogglePurchased={setShowPurchasedOnly}
      />

      {/* Pan/Zoom Canvas */}
      <TransformWrapper
        ref={transformRef}
        initialScale={initialZoom}
        minScale={galleryConfig.canvas.minZoom}
        maxScale={isMobile ? 2.5 : galleryConfig.canvas.maxZoom}
        limitToBounds={false}
        centerOnInit={true}
        initialPositionX={initialPosX}
        initialPositionY={initialPosY}
        onTransformed={handleTransform}
        wheel={{
          step: galleryConfig.canvas.zoomStep,
          smoothStep: 0.004 // Smoother wheel zoom
        }}
        panning={{
          velocityDisabled: false,
          lockAxisX: false,
          lockAxisY: false
        }}
        pinch={{ step: 5 }} // Smooth pinch zoom on mobile
        doubleClick={{ disabled: false, step: 0.5 }} // Double click to zoom
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <>
            {/* Zoom Controls */}
            <ZoomControls
              onZoomIn={() => zoomIn()}
              onZoomOut={() => zoomOut()}
              onReset={() => resetTransform()}
              currentZoom={transform.scale}
            />

            {/* Canvas Content */}
            <TransformComponent
              wrapperStyle={{
                width: '100%',
                height: '100%'
              }}
              contentStyle={{
                width: canvasDimensions.width,
                height: canvasDimensions.height
              }}
            >
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  willChange: 'transform' // GPU acceleration hint
                }}
              >
                {/* VIRTUALIZATION: Only render visible images (~100 instead of 10,000) */}
                {visibleImagesWithIndex.map(({ image, index, position }) => (
                  <MemoizedGalleryImage
                    key={image.id}
                    image={image}
                    position={position}
                    isVisible={true}
                    currentScale={transform.scale}
                    onClick={() => handleImageClick(index)}
                  />
                ))}
              </div>
            </TransformComponent>
          </>
        )}
      </TransformWrapper>

      {/* Instructions tooltip - fades out after 3 seconds */}
      <div className="absolute bottom-6 left-6 z-40 pointer-events-none animate-fade-out">
        <div className="bg-black/60 backdrop-blur-sm text-white text-xs rounded-lg px-3 py-2">
          <span className="hidden sm:inline">Scroll to zoom | Drag to pan</span>
          <span className="sm:hidden">Pinch to zoom | Drag to pan</span>
        </div>
      </div>

      {/* Image count indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-40 pointer-events-none">
        <div className="bg-black/40 text-white/70 text-xs rounded-full px-3 py-1">
          {displayImages.length} photo{displayImages.length !== 1 ? 's' : ''}{showLikedOnly ? ' liked' : ''}
        </div>
      </div>

      {/* Lightbox viewer */}
      <Lightbox
        images={displayImages}
        state={lightbox.state}
        onClose={lightbox.close}
      />

      <style>{`
        @keyframes fadeOut {
          0% { opacity: 1; }
          70% { opacity: 1; }
          100% { opacity: 0; }
        }
        .animate-fade-out {
          animation: fadeOut 4s ease-out forwards;
        }
      `}</style>
    </div>
  )
}
